import { LogManager } from '@src/utility/Logger';

import {
  makeTranslatedValueResolver,
  resolvePhraseCaptionTexts,
} from '../../services/karaokeSegmentation';
import { computeTranslatedCaptionTextPatches } from '../../services/syncTranslatedCaptionText';
import { AbstractCommand } from '../AbstractCommand';
import { MergeAdjacentCaptionClipsCommand } from './MergeAdjacentCaptionClipsCommand';

import type { KaraokeClip } from '../../models/karaoke/KaraokeClip';
import type { PhraseSoftBreak } from '../../services/captionSoftBreaks';
import type { CaptionPhraseEntry } from '../../services/syncTranslatedCaptionText';
import type { VideoEditor } from '../../VideoEditor';

const logger = LogManager.getLogger('karaoke:syncTranslatedText');

const ORIGINAL_LANGUAGE = 'original';

/**
 * Push the current translation phrases' text into the EXISTING translated
 * karaoke track, leaving its structure alone.
 *
 * This is the non-destructive counterpart to re-running
 * `ProduceKaraokeCommand` after a translation edit. A re-produce rebuilds the
 * track from the producer's segmentation, which silently discards the user's
 * caption-structure edits — synthetic line breaks and merged rows (a merge
 * leaves no record at all, so it cannot be replayed). This command touches only
 * `translatedText` and `translatedPhrases` on each clip — plus its soft line
 * breaks, which index into `translatedPhrases` (PLAT-11914) — so clip count,
 * boundaries, token arrays, time offsets and sibling groups all survive.
 *
 * Undo restores exactly those fields per clip — not a whole-timeline
 * snapshot — so it can't disturb anything else on the track.
 *
 * The one structural exception is a COLLAPSE: when an edit leaves a phrase with
 * fewer words than the rows it is split across, that line break has stopped
 * being representable, so the surplus rows are merged away (via
 * `MergeAdjacentCaptionClipsCommand`, which also retires the matching
 * `CaptionLineBreak` record) and the sync re-planned against the collapsed
 * track. Those merges run as sub-commands — executed directly, undone here in
 * reverse — so the whole sync stays a single reversible unit.
 */
export class SyncTranslatedCaptionTextCommand extends AbstractCommand {
  private _shouldCreateUndoRedoEntry: boolean;
  private changed = false;
  private previous: {
    clip: KaraokeClip;
    translatedText?: string;
    translatedPhrases?: CaptionPhraseEntry[];
    softBreakPhrasePositions?: PhraseSoftBreak[];
  }[] = [];
  private collapses: MergeAdjacentCaptionClipsCommand[] = [];

  constructor(editor: VideoEditor, createUndoRedoEntry: boolean = true) {
    super(editor);
    this._shouldCreateUndoRedoEntry = createUndoRedoEntry;
  }

  getName(): string {
    return 'SyncTranslatedCaptionTextCommand';
  }

  getDescription(): string {
    return 'Sync translated caption text from translation phrases';
  }

  /**
   * PLAT-11877 — the caller's choice, known before `execute()`: both hosts
   * snapshot (and the clip preview autosaves) on the answer they get before
   * running a command, so "only once something changed" meant Refresh
   * Captions was never undoable or saved. A refresh that changes nothing
   * leaves an empty undo step.
   */
  shouldCreateUndoRedoEntry(): boolean {
    return this._shouldCreateUndoRedoEntry;
  }

  execute(): boolean {
    const track = this.editor.getKaraokeTrack();
    if (!track) {
      logger.warn('execute: no karaoke track');
      return false;
    }

    const language = this.editor.getDefaultKaraokeConfig()?.language;
    if (!language || language === ORIGINAL_LANGUAGE) {
      // Original-language captions don't read translations — nothing to sync.
      return false;
    }

    const phrases = this.editor.getTranslationPhrasesForLanguage(language);
    if (!phrases || phrases.length === 0) {
      logger.warn('execute: no translation phrases for language', { language });
      return false;
    }

    // Ask the PRODUCER what text each phrase's row should hold, rather than
    // reading `phrase.translatedValue` — it folds in punctuation that gets no row
    // of its own, and re-deriving that here is what made every folded row look
    // permanently stale while a sync silently deleted the punctuation.
    const captionTextByLeadIndex = resolvePhraseCaptionTexts(
      this.editor.getActiveTranscriptNew() ?? [],
      phrases,
      makeTranslatedValueResolver(phrases),
    );
    const textSource = { captionTextByLeadIndex };

    let plan = computeTranslatedCaptionTextPatches(
      track.karaokeClips,
      phrases,
      textSource,
    );

    if (plan.collapseClipIndices.length > 0) {
      // Resolve every id BEFORE the first merge — indices shift as clips
      // combine. Merging keeps the LEFT clip's id, so the right-hand ids
      // collected here stay valid through the whole loop.
      const collapseIds = plan.collapseClipIndices
        .map(index => track.karaokeClips[index]?.id)
        .filter((id): id is string => !!id);
      for (const id of collapseIds) {
        // Sub-command: executed directly, not via `editor.executeCommand`, so
        // this command owns the undo (matches `SplitElementCommand`).
        const collapse = new MergeAdjacentCaptionClipsCommand(this.editor, id);
        if (collapse.execute()) this.collapses.push(collapse);
      }
      logger.warn('collapsed caption rows that no longer fit their phrase', {
        language,
        requested: collapseIds.length,
        collapsed: this.collapses.length,
      });
      // Re-plan against the collapsed track: the rows now fit, so the phrases
      // that were skipped above get their text on this pass.
      plan = computeTranslatedCaptionTextPatches(
        track.karaokeClips,
        phrases,
        textSource,
      );
      // Deliberately no second collapse round — one pass is enough because
      // `planCollapse` reduces each phrase to exactly the row count its text can
      // fill. A leftover plan therefore means a merge REFUSED (e.g. the row had
      // no left neighbour), and those phrases keep their old text. Surface it
      // rather than dropping it silently; looping here could not fix it.
      if (plan.collapseClipIndices.length > 0) {
        logger.warn('rows still uncollapsible after merging; text left stale', {
          language,
          remaining: plan.collapseClipIndices.length,
        });
      }
    }

    const { patches, staleTooShort } = plan;

    if (staleTooShort > 0) {
      // Text that did not fit AND whose rows were not safely collapsible (see
      // `planCollapse`). Those rows keep their previous text rather than one of
      // them rendering blank.
      logger.warn('phrases left stale: edited text shorter than its rows', {
        language,
        phraseCount: staleTooShort,
      });
    }

    patches.forEach((patch, index) => {
      if (!patch) return;
      const clip = track.karaokeClips[index];
      this.previous.push({
        clip,
        translatedText: clip.translatedText,
        translatedPhrases: clip.translatedPhrases?.map(entry => ({ ...entry })),
        softBreakPhrasePositions: clip.softBreakPhrasePositions,
      });
      clip.translatedText = patch.translatedText;
      clip.translatedPhrases = patch.translatedPhrases;
      clip.softBreakPhrasePositions = patch.softBreakPhrasePositions;
    });

    this.changed = this.previous.length > 0 || this.collapses.length > 0;
    if (this.changed) {
      this.editor.markDirty('translated_caption_text_synced');
    }
    return true;
  }

  undo(): boolean {
    // Reverse of execute: text first, then the collapses in reverse order. A
    // collapse's undo restores the pre-merge clip array (whose original clips
    // were never text-patched), so the two steps can't fight.
    for (const {
      clip,
      translatedText,
      translatedPhrases,
      softBreakPhrasePositions,
    } of this.previous) {
      clip.translatedText = translatedText;
      clip.translatedPhrases = translatedPhrases;
      clip.softBreakPhrasePositions = softBreakPhrasePositions;
    }
    for (let i = this.collapses.length - 1; i >= 0; i--) {
      this.collapses[i].undo();
    }
    if (this.changed) {
      this.editor.markDirty('translated_caption_text_sync_undo');
    }
    return true;
  }
}
